// src/components/services/AILegalResearcher/QueryDetails/QueryDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Clock, Hash, ChevronDown, ChevronUp, Share2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { useAuth } from '../../../../contexts/AuthContext';
import { createApi } from '../../../../services/api';
import MainLayout from '../../../Layout/MainLayout';
import RelatedCases from '../RelatedCases';
import ExportMenu from '../../../ExportMenu/ExportMenu';
import ExportStatusBadge from './ExportStatusBadge';

// Custom components for ReactMarkdown
const MarkdownComponents = {
    p: ({ children }) => <p className="mb-4 text-gray-700">{children}</p>,
    h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-gray-900">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-bold mb-3 text-gray-900">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-bold mb-2 text-gray-900">{children}</h3>,
    ul: ({ children }) => <ul className="list-disc pl-6 mb-4 text-gray-700">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 text-gray-700">{children}</ol>,
    li: ({ children }) => <li className="mb-1">{children}</li>,
    blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-gray-200 pl-4 py-2 mb-4 italic text-gray-700">
            {children}
        </blockquote>
    ),
    code: ({ node, inline, className, children, ...props }) => {
        if (inline) {
            return (
                <code className="bg-gray-100 rounded px-1 py-0.5 text-sm text-gray-800" {...props}>
                    {children}
                </code>
            );
        }
        return (
            <pre className="bg-gray-100 rounded p-4 mb-4 overflow-x-auto">
                <code className="text-sm text-gray-800 block" {...props}>
                    {children}
                </code>
            </pre>
        );
    },
    a: ({ children, href }) => (
        <a href={href} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    ),
    table: ({ children }) => (
        <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200">
                {children}
            </table>
        </div>
    ),
    th: ({ children }) => (
        <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {children}
        </td>
    ),
};

const QueryDetails = () => {
    const { questionId } = useParams();
    const [query, setQuery] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [creditBalance, setCreditBalance] = useState(null);
    const [relatedCasesExpanded, setRelatedCasesExpanded] = useState(true);
    const [shareMessage, setShareMessage] = useState(null);

    useEffect(() => {
        if (token) {
            fetchBalance();
            fetchQueryDetails();
        }
    }, [questionId, token]);

    const fetchBalance = async () => {
        try {
            const api = createApi(token);
            const response = await api.getBalance();
            setCreditBalance(response.balance);
        } catch (err) {
            console.error('Error fetching balance:', err);
        }
    };

    const fetchQueryDetails = async () => {
        try {
            setIsLoading(true);
            const api = createApi(token);
            const response = await api.get(`/question-detail/${questionId}/`);

            if (!response) {
                throw new Error('No data received from API');
            }

            if (response.id || response.question) {
                setQuery(response);
            } else if (response.data && (response.data.id || response.data.question)) {
                setQuery(response.data);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (err) {
            console.error('Error details:', err);
            setError(err.message || 'Failed to load query details');
            setQuery(null);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed':
                return 'bg-green-100 text-green-800';
            case 'Processing':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-red-100 text-red-800';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const handleShareLink = async () => {
        try {
            const url = window.location.href;
            
            if (navigator.share) {
                await navigator.share({
                    title: 'eWakili Legal Research',
                    text: `Check out this legal research: ${query?.question}`,
                    url: url
                });
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(url);
                setShareMessage('Link copied to clipboard!');
                setTimeout(() => setShareMessage(null), 3000);
            }
        } catch (error) {
            console.error('Error sharing:', error);
            setShareMessage('Failed to share link');
            setTimeout(() => setShareMessage(null), 3000);
        }
    };

    if (isLoading) {
        return (
            <MainLayout creditBalance={creditBalance}>
                <div className="max-w-5xl mx-auto p-6">
                    <div className="animate-pulse space-y-6">
                        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-40 bg-gray-200 rounded"></div>
                        <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (error) {
        return (
            <MainLayout creditBalance={creditBalance}>
                <div className="max-w-5xl mx-auto p-6">
                    <button
                        onClick={() => navigate('/history')}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
                    >
                        <ArrowLeft className="h-5 w-5 mr-2" />
                        Back to History
                    </button>
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (!query) {
        return (
            <MainLayout creditBalance={creditBalance}>
                <div className="max-w-5xl mx-auto p-6">
                    <button
                        onClick={() => navigate('/history')}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
                    >
                        <ArrowLeft className="h-5 w-5 mr-2" />
                        Back to History
                    </button>
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                        <div className="text-6xl mb-4">🔍</div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">Query not found</h3>
                        <p className="text-gray-500">We couldn't find the research query you're looking for.</p>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout creditBalance={creditBalance}>
            <div className="max-w-5xl mx-auto px-4 py-6">
                <button
                    onClick={() => navigate('/history')}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
                >
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Back to History
                </button>

                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold text-white mb-2">Research Query</h1>
                                <div className="flex items-center text-blue-100 text-sm space-x-3">
                                    <span className="flex items-center">
                                        <Clock className="h-4 w-4 mr-1" />
                                        {formatDate(query.date_created)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(query.status)}`}>
                                    {query.status}
                                </span>
                                
                                <div className="flex gap-2">
                                    {/* Share Button */}
                                    <button
                                        onClick={handleShareLink}
                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                                        aria-label="Share link"
                                    >
                                        <Share2 className="h-4 w-4 mr-1" />
                                        <span className="hidden sm:inline">Share</span>
                                    </button>
                                    
                                    {/* Export Menu with Status Badge */}
                                    <div className="relative">
                                        <ExportMenu queryData={query} />
                                        <ExportStatusBadge status={query.status} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Share message notification */}
                        {shareMessage && (
                            <div className="mt-3 px-3 py-2 bg-white/20 text-white text-sm rounded animate-fade-in">
                                {shareMessage}
                            </div>
                        )}
                    </div>

                    {/* Query Content */}
                    <div className="p-6 space-y-8">
                        {/* Question */}
                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                <div className="h-2 w-2 rounded-full bg-blue-500 mr-2"></div>
                                Question
                            </h2>
                            <p className="text-gray-700">{query.question}</p>

                            {query.question_type && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {query.question_type}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Answer */}
                        {query.result && (
                            <div className="bg-white rounded-lg p-5 border border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                    <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                                    Answer
                                </h2>
                                <div className="prose prose-sm max-w-none markdown-content">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm, remarkBreaks]}
                                        components={MarkdownComponents}
                                    >
                                        {query.result}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        )}

                        {/* Similar Cases */}
                        {query.similar_cases && query.similar_cases.length > 0 && (
                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                <div className="flex justify-between items-center p-5 border-b border-gray-200 bg-indigo-50">
                                    <h2 className="text-lg font-semibold text-indigo-900">Similar Cases</h2>
                                    <button
                                        onClick={() => setRelatedCasesExpanded(!relatedCasesExpanded)}
                                        className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm font-medium transition-colors"
                                    >
                                        {relatedCasesExpanded ? (
                                            <>Hide <ChevronUp className="h-4 w-4 ml-1" /></>
                                        ) : (
                                            <>Show <ChevronDown className="h-4 w-4 ml-1" /></>
                                        )}
                                    </button>
                                </div>
                                {relatedCasesExpanded && (
                                    <div className="p-5">
                                        <RelatedCases 
                                            cases={query.similar_cases}
                                            onViewCase={(url) => window.open(url, '_blank')}
                                            onDownloadCase={(url) => {
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.download = 'case-document.pdf';
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Query Stats */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                    <p className="text-xs text-gray-500 mb-1">Credits Used</p>
                                    <p className="text-lg font-medium text-indigo-700 flex items-center">
                                        <Hash className="h-4 w-4 mr-1 text-indigo-400" />
                                        {query.charges || 'N/A'}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                    <p className="text-xs text-gray-500 mb-1">Processing Time</p>
                                    <p className="text-lg font-medium text-amber-700 flex items-center">
                                        <Clock className="h-4 w-4 mr-1 text-amber-400" />
                                        {query.completion_time ? `${query.completion_time.toFixed(2)}s` : 'N/A'}
                                    </p>
                                </div>
                                
                                {/* Add additional stats if available */}
                                {query.similar_cases && (
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                        <p className="text-xs text-gray-500 mb-1">Similar Cases</p>
                                        <p className="text-lg font-medium text-blue-700">
                                            {query.similar_cases.length}
                                        </p>
                                    </div>
                                )}
                                
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                    <p className="text-xs text-gray-500 mb-1">Query ID</p>
                                    <p className="text-sm font-mono text-gray-700">
                                        {String(query.id).substring(0, 8)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default QueryDetails;